'use client';

import { useSearchParams, useRouter } from 'next/navigation';
import styled from '@emotion/styled';
import { useRef } from 'react';
import SearchIcon from '../../../assets/icons/search.svg';
import { useSearchData } from '../../../common-data/useSearchData';
import { SearchOverlayDialog } from '../../../features/content-search/ui/SearchOverlayDialog';
import SecondaryNavigationButton from '../navigation/shared/SecondaryNavigationButton';
import { Css, Responsive } from '../../../ui';
import { useCreateUnifiedSearchStores } from './domain/hooks/useCreateUnifiedSearchStores';
import { DEBOUNCE_TIME_MS, QUERY_MIN_LENGTH } from './domain/constants';
import { useDebouncedQuery } from './domain/hooks/useDebouncedQuery';

export default function GlobalSearch() {
	const { isSearchOpen, updateSearchOpen } = useSearchData();
	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		updateSearchOpen(!isSearchOpen);
	};

	const searchInputRef = useRef<HTMLInputElement>(null);
	const isMobile = !!Responsive.useMatchesBreakpoint('tablet');
	const hideMobileKeyboard = () => {
		if (isMobile && searchInputRef.current) {
			searchInputRef.current.blur(); // Blurs the input field to hide the keyboard
		}
	};

	const router = useRouter();
	const searchParams = useSearchParams();
	const { query, pendingQuery, updateQuery } = useDebouncedQuery({
		debounceTimeMs: DEBOUNCE_TIME_MS,
		queryMinLength: QUERY_MIN_LENGTH,
		onSubmitQuery: (newQuery) => {
			if ((searchParams.get('search') ?? '') !== newQuery) {
				const newSearchParam = new URLSearchParams(searchParams);
				newSearchParam.set('search', newQuery);
				const newUrl = `?${newSearchParam.toString()}`;
				router.push(newUrl);
			}

			if (newQuery !== '') hideMobileKeyboard();
		},
	});

	const { flightStore, locationStore, informationStore } =
		useCreateUnifiedSearchStores(query);

	// we are debouncing when the pending query and active query do not match
	// we check again informationStore.userQuery instead of query to ensure the timing works out
	const isDebouncing =
		informationStore.userQuery !== pendingQuery &&
		pendingQuery.length >= QUERY_MIN_LENGTH;

	return (
		<>
			<StyledSecondaryNavButton
				onClick={handleClick}
				aria-label="Search"
				aria-expanded={isSearchOpen}
				icon={SearchIcon}
			>
				{null}
			</StyledSecondaryNavButton>
			<SearchOverlayDialog
				flightStore={flightStore}
				locationStore={locationStore}
				informationStore={informationStore}
				query={pendingQuery}
				updateQuery={updateQuery}
				searchInputRef={searchInputRef}
				isDebouncing={isDebouncing}
			/>
		</>
	);
}

const StyledSecondaryNavButton = styled(SecondaryNavigationButton)`
	height: ${Css.pxToRem(48)};
	width: ${Css.pxToRem(48)};
`;
